import React from "react";
import { ArrowUpRight } from "lucide-react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import { BOTANICAL_SRC } from "../components/BotanicalOverlay";

const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

// Photorealistic fine-art hero photograph, generated for this page:
// Black woman seated in an ornate wire birdcage on a floating wooden platform
// on still reflective water, cage door ajar, warm cinematic sunrise.
const HERO_IMG = "/generated/emdr-hero-birdcage.png";

const Eyebrow = ({ children, className = "" }) => (
    <span className={`editorial-eyebrow ${className}`}>{children}</span>
);

const Hairline = ({ color = "#8A9E85" }) => (
    <div style={{ height: 2, width: 64, background: color }} className="my-6" />
);

const CTA = ({ children = "Schedule a Consultation", testid }) => (
    <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="editorial-button-primary group"
    >
        {children}
        <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
);

// — Sections —

const Hero = () => (
    <section data-testid="emdr-hero" className="relative pt-40 pb-16 lg:pt-48 lg:pb-20 overflow-hidden" style={{ background: "#FAFAF8" }}>
        <div className="relative mx-auto max-w-[1280px] px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Text — same centered-left position within its column */}
                <div className="lg:col-span-6 text-center lg:text-left" data-reveal>
                    <Eyebrow>EMDR Therapy</Eyebrow>
                    <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[74px] leading-[1.02] text-charcoal text-balance">
                        EMDR Therapy for the Patterns That <em className="italic text-gold">Keep You Stuck.</em>
                    </h1>
                    <div className="mt-8 mx-auto lg:mx-0" style={{ height: 2, width: 64, background: "#8A9E85" }} />
                    <p className="mt-10 max-w-2xl mx-auto lg:mx-0 text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">
                        You have done the work of understanding. Something deeper is still holding on.
                    </p>
                    <div className="mt-12">
                        <CTA testid="emdr-hero-cta" />
                    </div>
                </div>

                {/* Photograph — fine-art hero, generated for this page */}
                <div className="lg:col-span-6 hidden lg:block" data-reveal data-testid="emdr-hero-image-wrap">
                    <img
                        src={HERO_IMG}
                        alt="A woman in flowing white fabric sits peacefully inside an ornate open wire birdcage on a floating platform over still water at sunrise"
                        data-testid="emdr-hero-image"
                        className="w-full h-auto object-cover"
                        style={{ borderRadius: "2px", boxShadow: "0 24px 60px -32px rgba(44,40,37,0.28)", maxHeight: "620px" }}
                        loading="eager"
                    />
                </div>
            </div>
        </div>
    </section>
);

/* =====================================================================
   Thin botanical divider — a fine hairline with a small floral motif at
   the center, sitting between the hero and the "What Brings You Here"
   section. Matches the delicate line-art vocabulary used elsewhere.
   ===================================================================== */
const BotanicalDivider = () => (
    <div data-testid="emdr-hero-divider" className="relative flex items-center justify-center py-14" style={{ background: "#FAFAF8" }}>
        <svg
            viewBox="0 0 720 40"
            className="w-full max-w-[820px] px-6"
            aria-hidden
            style={{ opacity: 0.7 }}
        >
            <g stroke="#9B7D5A" strokeWidth="0.9" fill="none" strokeLinecap="round">
                {/* left rule */}
                <line x1="10" y1="20" x2="290" y2="20" />
                {/* right rule */}
                <line x1="430" y1="20" x2="710" y2="20" />

                {/* central floral motif */}
                <path d="M360 8 C 360 14, 360 26, 360 32" />
                <ellipse cx="360" cy="12" rx="6" ry="3" transform="rotate(-25 360 12)" />
                <ellipse cx="360" cy="20" rx="10" ry="4" />
                <ellipse cx="360" cy="28" rx="6" ry="3" transform="rotate(25 360 28)" />

                {/* small leaves flanking the motif */}
                <path d="M330 20 q 8 -6 20 0" />
                <path d="M330 20 q 8 6 20 0" />
                <path d="M390 20 q 8 -6 20 0" />
                <path d="M390 20 q 8 6 20 0" />

                {/* tiny stars at the far ends */}
                <path d="M4 20 l 3 -3 l 3 3 l -3 3 z" fill="#9B7D5A" opacity="0.9" />
                <path d="M710 20 l 3 -3 l 3 3 l -3 3 z" fill="#9B7D5A" opacity="0.9" />
            </g>
        </svg>
    </div>
);

const Problem = () => (
    <section data-testid="emdr-problem" className="relative py-28 lg:py-36 overflow-hidden" style={{ background: "#FAFAF8" }}>
        {/* Botanical edge washes — opacity bumped ~25% (0.08 → 0.10) so they read as intentional decoration */}
        <div
            aria-hidden
            data-testid="emdr-problem-botanical-left"
            className="absolute left-0 top-0 bottom-0 hidden lg:block pointer-events-none"
            style={{
                width: "34%",
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left center",
                backgroundSize: "contain",
                opacity: 0.10,
                mixBlendMode: "multiply",
                WebkitMaskImage: "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 65%, transparent 100%)",
                maskImage: "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 65%, transparent 100%)",
            }}
        />
        <div
            aria-hidden
            data-testid="emdr-problem-botanical-right"
            className="absolute right-0 top-0 bottom-0 hidden lg:block pointer-events-none"
            style={{
                width: "34%",
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right center",
                backgroundSize: "contain",
                opacity: 0.10,
                mixBlendMode: "multiply",
                transform: "scaleX(-1)",
                WebkitMaskImage: "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 65%, transparent 100%)",
                maskImage: "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.55) 65%, transparent 100%)",
            }}
        />

        {/* Widened content column — was max-w-[800px], now uses more of the available page width */}
        <div className="relative mx-auto max-w-[1120px] px-6 lg:px-16 text-center" data-reveal>
            <Eyebrow>What Brings You Here</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-charcoal text-balance">
                When You Cannot Think Your Way <em className="italic text-gold">Out of It</em> Anymore
            </h2>
            <div className="mx-auto mt-8" style={{ height: 2, width: 64, background: "#8A9E85" }} />

            <div className="mt-14 text-left mx-auto max-w-[960px]">
                <p className="text-2xl sm:text-3xl font-serif italic text-charcoal drop-cap">
                    You analyze everything.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    You replay conversations in your head. You overprepare. You question yourself after
                    making decisions. You tell yourself that once you feel more confident, more certain,
                    or more emotionally ready, then you will finally move forward.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    What is often happening underneath these patterns is <em className="italic">hypervigilance</em> —
                    a nervous system that learned, somewhere along the way, that staying alert was how
                    you stayed safe. The overthinking, the overpreparing, the constant scanning for what
                    could go wrong: these are not personality flaws or signs that something is wrong with
                    you. They are responses that were shaped in you, often by difficult or painful
                    experiences, and they made sense at the time.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    Yet no matter how much insight you gain, the emotional reactions keep returning.
                    This is where EMDR becomes so useful — <em className="italic">for what talking alone cannot reach</em>.
                    Insight lives in the mind. These patterns live in the body and the nervous system.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    The same fears show up in relationships, opportunities, visibility, and the way
                    you see yourself.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    Part of you knows you are capable of more. Another part still feels emotionally stuck.
                </p>
            </div>
        </div>
    </section>
);

const HowItHelps = () => (
    <section data-testid="emdr-howithelps" className="relative py-28 lg:py-36 overflow-hidden" style={{ background: "#EEF2ED" }}>
        {/* Subtle botanical line art overlay — matches the delicate style used on the tarot pages */}
        <div
            aria-hidden
            data-testid="emdr-howithelps-botanical"
            className="absolute inset-0 pointer-events-none"
            style={{
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right -80px bottom -60px",
                backgroundSize: "62%",
                opacity: 0.09,
                mixBlendMode: "multiply",
                WebkitMaskImage: "radial-gradient(ellipse at 90% 90%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
                maskImage: "radial-gradient(ellipse at 90% 90%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
            }}
        />
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left -120px top -40px",
                backgroundSize: "48%",
                opacity: 0.07,
                mixBlendMode: "multiply",
                transform: "scaleX(-1)",
                WebkitMaskImage: "radial-gradient(ellipse at 10% 10%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
                maskImage: "radial-gradient(ellipse at 10% 10%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
            }}
        />

        <div className="relative mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-7" data-reveal>
                    <Eyebrow>The Approach</Eyebrow>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                        How EMDR Therapy <em className="italic text-gold">Helps</em>
                    </h2>
                    <Hairline />

                    {/* Plain-language EMDR explainer — sits at the top of "The Approach" */}
                    <p className="mt-8 text-lg leading-[1.95] text-charcoal-soft">
                        EMDR stands for <em className="italic">Eye Movement Desensitization and Reprocessing</em>.
                        It is a structured, evidence-based therapy that helps the brain and nervous system
                        process memories and experiences that are still causing distress, so they no longer
                        keep pulling you back into the same emotional reactions.
                    </p>

                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        EMDR helps process the experiences that continue to shape anxiety, fear, self-doubt,
                        and emotional reactivity.
                    </p>
                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        Often, the patterns that feel automatic today began long ago. Your brain learned how
                        to protect you through hesitation, perfectionism, overthinking, or staying small.
                        Those patterns may have once helped you survive emotionally difficult experiences,
                        but now they may be limiting your life.
                    </p>
                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        EMDR helps the brain and nervous system process those experiences differently so
                        they no longer carry the same emotional intensity.
                    </p>
                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        Many clients notice that situations which once felt overwhelming begin to feel
                        manageable. Fear loosens its grip. Opportunities feel less emotionally threatening.
                        They stop feeling trapped in the same internal reactions.
                    </p>
                </div>

                <aside className="lg:col-span-5 lg:pt-16" data-reveal>
                    <blockquote
                        style={{ borderLeft: "3px solid #9B7D5A", paddingLeft: "24px" }}
                        className="font-serif italic text-charcoal"
                    >
                        <p className="text-xl sm:text-2xl leading-[1.45]" style={{ fontSize: "24px" }}>
                            "What I love about EMDR is how deeply transformative it can be. Clients are
                            often surprised by how much movement can happen in a relatively short amount
                            of time. Work that may take months in traditional talk therapy can sometimes
                            begin shifting within a single EMDR session."
                        </p>
                        <footer className="mt-6 text-[11px] tracking-[0.32em] uppercase not-italic" style={{ color: "#9B7D5A", fontFamily: "Figtree" }}>
                            <span className="mr-3">✦</span> Austine, LPC
                        </footer>
                    </blockquote>
                </aside>
            </div>
        </div>
    </section>
);

const ThePractice = () => (
    <section data-testid="emdr-practice" className="py-28 lg:py-36" style={{ background: "#F2EDE6" }}>
        <div className="mx-auto max-w-[1120px] px-6 lg:px-16 text-center" data-reveal>
            <Eyebrow>The Practice</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                Intentional Therapy Designed for <em className="italic text-gold">Lasting Change</em>
            </h2>
            <div className="mx-auto mt-8" style={{ height: 2, width: 64, background: "#8A9E85" }} />

            <div className="mt-12 text-left mx-auto max-w-[960px]">
                <p className="text-lg leading-[1.95] text-charcoal-soft">
                    Alchemy Practice is a depth-focused therapy practice designed for people who want more
                    than temporary relief. Alchemy Practice offers EMDR therapy to clients throughout
                    Wisconsin and Colorado via secure telehealth, with focused availability for
                    thoughtful adults looking for meaningful, lasting change.
                </p>
                <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                    I use a phased approach to therapy that creates structure, momentum, and long-term
                    support. In the beginning, weekly sessions help establish emotional safety and
                    consistency while we begin processing deeper experiences. As clients begin feeling
                    more grounded and confident, many naturally transition into biweekly or monthly
                    sessions focused on integration and continued growth.
                </p>
                <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                    The goal is not dependence on therapy. The goal is helping you build a life where
                    fear no longer quietly shapes your direction.
                </p>
                <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                    EMDR is one of several depth-oriented modalities offered here. If you would like to
                    see how it fits alongside <a href="/approach" className="underline decoration-gold underline-offset-4 hover:text-gold" data-testid="emdr-link-approach">the full approach and modalities</a>,
                    or if longer, less frequent work sounds like a better fit, you can also read about
                    <a href="/emdr-intensive" className="underline decoration-gold underline-offset-4 hover:text-gold ml-1" data-testid="emdr-link-intensive">EMDR Intensives</a>.
                </p>
            </div>
        </div>
    </section>
);

const beginSteps = [
    {
        n: "I",
        title: "History, Grounding, and Preparation",
        body: "Before any processing begins, we slow down together. I take a careful history to understand what has shaped your patterns and nervous system responses. We build grounding and resourcing skills so you feel supported throughout the work. Then we identify the specific memories, beliefs, or experiences we will target.",
    },
    {
        n: "II",
        title: "Bilateral Stimulation and Active Processing",
        body: "This is the heart of EMDR. Using bilateral stimulation, which can be done through guided eye movements, gentle tapping, or auditory tones, we work directly with the targeted memory or belief. The bilateral input allows the brain and nervous system to process the experience differently, reducing the emotional intensity it still carries.",
    },
    {
        n: "III",
        title: "Installing New Beliefs and Integration",
        body: "Once the emotional charge of a memory has shifted, we install the positive belief you want to carry forward. We check in with the body, close each session safely, and over time, what once felt like an automatic reaction begins to feel like a choice.",
    },
];

const BeginTogether = () => (
    <section data-testid="emdr-begin" className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="text-center max-w-2xl mx-auto" data-reveal>
                <Eyebrow>How We'll Begin Together</Eyebrow>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                    A process that unfolds with <em className="italic text-gold">care.</em>
                </h2>
                <div className="mx-auto mt-8" style={{ height: 2, width: 64, background: "#8A9E85" }} />
            </div>

            <div className="mt-20 grid md:grid-cols-3 gap-px" style={{ background: "#E2DDD8" }} data-testid="emdr-begin-grid">
                {beginSteps.map((s) => (
                    <article
                        key={s.title}
                        data-testid={`emdr-step-${s.n.toLowerCase()}`}
                        className="p-10 lg:p-12 group"
                        style={{ background: "#FAFAF8" }}
                        data-reveal
                    >
                        <div className="flex items-baseline justify-between mb-8">
                            <span className="font-serif text-3xl text-gold">{s.n}</span>
                            <span className="font-serif text-gold text-lg leading-none transition-transform duration-700 group-hover:rotate-90">✦</span>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl text-charcoal leading-snug">{s.title}</h3>
                        <div className="h-px w-10 my-6" style={{ background: "#8A9E85" }} />
                        <p className="text-charcoal-soft leading-[1.85] text-base">{s.body}</p>
                    </article>
                ))}
            </div>

            <div className="text-center mt-16" data-reveal>
                <CTA testid="emdr-begin-cta" />
                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[11px] tracking-[0.28em] uppercase" style={{ color: "#8A7968" }}>
                    <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Secure Telehealth</span>
                    <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Wisconsin & Colorado</span>
                </div>
            </div>
        </div>
    </section>
);

const emdrPhrases = [
    "Fear loosens its grip.",
    "You are capable of more than anxiety has allowed you to believe.",
    "EMDR works where insight alone cannot reach.",
    "Healing is not about becoming someone new. It is about returning to who you already are.",
    "The emotional reactions that feel automatic today can change.",
];

// Service-level schema — reinforces that this page describes a specific
// medical therapy offered by Alchemy Practice across Wisconsin and Colorado.
const emdrJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    name: "EMDR Therapy",
    alternateName: "Eye Movement Desensitization and Reprocessing",
    description:
        "Structured, evidence-based EMDR therapy for anxiety, fear, self-doubt, and unresolved emotional experiences. Delivered virtually to clients throughout Wisconsin and Colorado by Alchemy Practice.",
    url: "https://alchemypractice.com/emdr",
    provider: {
        "@type": "MedicalBusiness",
        "@id": "https://alchemypractice.com/#business",
        name: "Alchemy Practice",
    },
    areaServed: [
        { "@type": "State", name: "Wisconsin" },
        { "@type": "State", name: "Colorado" },
    ],
    availableService: {
        "@type": "MedicalProcedure",
        procedureType: "Psychotherapy",
    },
};

export default function EMDRPage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="EMDR Therapy in Wisconsin & Colorado | Alchemy Practice"
                description="Virtual EMDR therapy for anxiety, fear, and self-doubt. Serving Madison, Fitchburg, and all of Wisconsin and Colorado via telehealth. Austine LPC."
                jsonLd={emdrJsonLd}
            />
            <Nav />
            <RevealOnScroll>
                <Hero />
                <BotanicalDivider />
                <Problem />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <HowItHelps />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ThePractice />
                <SectionDivider color="gold" bg="#F2EDE6" />
                <BeginTogether />
                <Marquee phrases={emdrPhrases} background="#BFA3B2" />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
